import { Box } from '@mui/material';
import React, { HTMLAttributes, useEffect, useMemo } from 'react';
import {
  ActorVariant,
  TtsVersionSelectOption,
} from '../../../../../server/graphql/__generated.types';
import { useUserContext } from '../../../../context/user/UserContext';
import {
  UserSettingsFragment,
  UserSettingsFragmentDoc,
  useUpdateUserSettingsMutation,
} from '../../../AccountUserSettings/operation.generated';
import SingleSelect from '../../../DropdownMenu/menus/SingleSelect';

export interface ActiveVariantVersionSelectorWrapperProps {
  activeVariant: ActorVariant;
  activeVariantVersion: TtsVersionSelectOption;
  setActiveVariantVersion: (
    variant: ActorVariant,
    label: string,
    modelVersion?: string
  ) => void;
  className?: HTMLAttributes<typeof Box>['className'];
}

const ActiveVariantVersionSelectorWrapper = ({
  activeVariant,
  activeVariantVersion,
  setActiveVariantVersion,
  className,
}: ActiveVariantVersionSelectorWrapperProps) => {
  const { data: userQuery } = useUserContext();

  const [updateVersionUserSetting] = useUpdateUserSettingsMutation({
    update: (cache, { data, errors }) => {
      if (!errors && data?.updateUserSettings.success) {
        const cachedSettings = cache.readFragment<UserSettingsFragment>({
          id: `User:${userQuery?.user.id}`,
          fragment: UserSettingsFragmentDoc,
        });

        if (cachedSettings) {
          const mergedSettings = {
            ...cachedSettings.settings,
            defaultTTSVersion:
              data.updateUserSettings.settings?.defaultTTSVersion,
          };

          cache.writeFragment<UserSettingsFragment>({
            id: `User:${userQuery?.user.id}`,
            fragment: UserSettingsFragmentDoc,
            data: { settings: mergedSettings },
          });
        }
      }
    },
  });

  const options = useMemo(() => {
    const hasNextVersion = Boolean(activeVariant.versions.next);
    const hasPreviousVersion = Boolean(activeVariant.versions.previous);

    const availableVersions: Partial<Record<TtsVersionSelectOption, string>> =
      {};
    if (hasNextVersion) {
      availableVersions.next = TtsVersionSelectOption.Next;
    }
    availableVersions.latest = TtsVersionSelectOption.Latest;
    if (hasPreviousVersion)
      availableVersions.previous = TtsVersionSelectOption.Previous;

    return availableVersions;
  }, [activeVariant.versions]);

  useEffect(() => {
    // From the available version options , set to user preference; otherwise set to latest since latest will always be an option
    const defaultValue = Object.values(options).find(
      version => version === activeVariantVersion?.toString()?.toLowerCase()
    );
    if (!defaultValue)
      setActiveVariantVersion(
        activeVariant,
        TtsVersionSelectOption.Latest,
        activeVariant.versions[TtsVersionSelectOption.Latest]?.model
      );
  }, [activeVariant, activeVariantVersion, options, setActiveVariantVersion]);

  const handleChange = (label: string) => {
    updateVersionUserSetting({
      variables: {
        input: {
          defaultTTSVersion: label as TtsVersionSelectOption,
        },
      },
    });
    setActiveVariantVersion(
      activeVariant,
      label,
      activeVariant.versions[label as TtsVersionSelectOption]?.model
    );
  };

  const getTTSVersionLabelDisplayName = (
    option: TtsVersionSelectOption | string
  ) => {
    return option === 'next' ? 'Beta' : option;
  };

  return (
    <>
      {userQuery?.user.settings.displayTTSVersionSelector && (
        <Box className={className}>
          <SingleSelect
            ariaLabel={'Select other language'}
            value={activeVariantVersion}
            items={Object.values(options).map(option => ({
              label: getTTSVersionLabelDisplayName(option ?? ''),
              value: getTTSVersionLabelDisplayName(option ?? ''),
            }))}
            onChange={item => handleChange(item.label)}
          />
        </Box>
      )}
    </>
  );
};

export default ActiveVariantVersionSelectorWrapper;
